import React, { useState } from "react";
import InputBox from "../../Dashboard/Components/InputBox";
import { useNavigate } from "react-router-dom";
import { setDocData } from "../../../utils/firebase/Firebase.utils";
import toast from "react-hot-toast";
import { v4 } from "uuid";
import { useSelector } from "react-redux";
import { FiX, FiCamera, FiUsers, FiPlus, FiInfo } from "react-icons/fi";

function CreateGroupChatForm({ createGroupChat }) {
  const currentUser = useSelector((state) => state.userObject);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });
  const [assignedId, setAssignedId] = useState(v4());
  const [groupImage, setGroupImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setGroupImage(file);
      const reader = new FileReader();
      reader.onload = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Don't proceed if name is empty
    if (!formData.name?.trim()) {
      toast.error("Group name is required");
      return;
    }

    // In a real app, you would upload the image to storage and get a URL
    // For now, we'll just use the imagePreview as a placeholder

    const groupChatData = {
      name: formData.name.trim(),
      description: formData.description || "", // Ensure description is never undefined
      members: [currentUser.uid],
      admins: [currentUser.uid],
      image: imagePreview || "",
      messages: [],
      createdAt: new Date(),
    };

    toast
      .promise(setDocData("groupChats", assignedId, groupChatData), {
        loading: "Creating your group...",
        success: "Group created successfully!",
        error: "Could not create group",
      })
      .then(() => {
        navigate("/group-chats/" + assignedId);
        createGroupChat(false);
      });
  };

  // Get initials for the group avatar placeholder
  const getInitials = (name) => {
    if (!name) return "G";
    return name
      .split(" ")
      .map((word) => word[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className="relative bg-white rounded-lg max-w-md w-full mx-auto overflow-hidden shadow-lg">
      {/* Header */}
      <div className="bg-[#363062] text-white px-5 py-4 flex items-center justify-between">
        <h2 className="text-lg font-medium">Create New Group</h2>
        <button
          onClick={createGroupChat}
          className="p-1.5 hover:bg-[#4A4179] rounded-full transition-colors"
        >
          <FiX size={20} />
        </button>
      </div>

      <div className="p-5">
        <form className="space-y-5" onSubmit={handleSubmit}>
          {/* Group Image Upload */}
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div
                className={`w-24 h-24 rounded-full flex items-center justify-center ${
                  imagePreview ? "" : "bg-[#363062]/10"
                }`}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="Group Preview"
                    className="w-24 h-24 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-24 h-24 rounded-full bg-[#363062] text-white flex items-center justify-center text-xl font-medium">
                    {getInitials(formData.name)}
                  </div>
                )}
              </div>
              <label className="absolute -right-1 bottom-0 w-8 h-8 bg-[#8FCD00] rounded-full flex items-center justify-center cursor-pointer shadow-md">
                <FiCamera size={16} className="text-white" />
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageChange}
                />
              </label>
            </div>
          </div>

          {/* Group Name */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group Name *
              </label>
              <input
                name="name"
                type="text"
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#363062] focus:border-[#363062]"
                placeholder="Enter group name"
                onChange={handleChange}
                value={formData.name}
                required
              />
            </div>

            {/* Group Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Group Description
              </label>
              <textarea
                name="description"
                rows="2"
                className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-[#363062] focus:border-[#363062]"
                placeholder="What's this group about?"
                onChange={handleChange}
                value={formData.description}
              />
            </div>

            {/* Participants Section (simplified as a future feature) */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">
                  Add Participants
                </label>
                <span className="text-xs text-gray-500">
                  You'll be added automatically
                </span>
              </div>

              <div className="p-4 bg-gray-50 rounded-lg border border-dashed border-gray-300 flex items-center justify-center">
                <div className="flex flex-col items-center text-gray-500">
                  <FiUsers size={20} className="mb-1" />
                  <span className="text-xs text-center">
                    You can add more participants after creating the group
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Create Button */}
          <div className="pt-4">
            <button
              type="submit"
              className="w-full px-4 py-3 bg-[#8FCD00] hover:bg-[#8FCD00]/90 text-white font-medium rounded-full shadow-sm transition-colors flex items-center justify-center"
            >
              Create Group
            </button>
          </div>

          {/* Privacy Note */}
          <div className="text-xs text-gray-500 text-center flex items-center justify-center mt-2">
            <FiInfo size={12} className="mr-1" />
            <span>Group members can see your profile information</span>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateGroupChatForm;